import type {
  dayInWeek,
  FormatBusinessHour,
  GetBusinessStatus,
  GetCurrentFullBusinessInfo,
  GetGroupedBusinessHours,
  GetNextBusinessPeriod,
  GetTodayBusinessHours,
} from './types';

export const getTodayBusinessHours: GetTodayBusinessHours = (openingHours) => { // @suppress longLineCheck
  const now = new Date();
  const currentDay: dayInWeek = now.getDay() as dayInWeek;

  const todayHours = openingHours.find(
    info => info.day === currentDay,
  );

  return todayHours!;
};

export const getBusinessStatus: GetBusinessStatus = (todayHours) => { // @suppress longLineCheck
  const now = new Date();
  const currentTime = now.getHours() * 60 + now.getMinutes();

  const [startHour, startMinute] = todayHours.startAt.split(':').map(Number);
  const [endHour, endMinute] = todayHours.endAt.split(':').map(Number);

  const openingTime = startHour * 60 + startMinute;
  const closingTime = endHour * 60 + endMinute;

  if (currentTime >= openingTime && currentTime < closingTime - 60) {
    return 'openNow';
  }

  if (currentTime >= closingTime - 60 && currentTime < closingTime) {
    return 'closingSoon';
  }

  if (currentTime < openingTime - 60) {
    return 'opensSoon';
  }
  return 'closedNow';
};

export const formatBusinessHour: FormatBusinessHour = (businesHour) => {
  const [hours, minutes] = businesHour.split(':').map(Number);

  const period = hours >= 12 ? 'pm' : 'am';

  const formattedHours = hours % 12 || 12;

  return `${formattedHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
};

export const getNextBusinessPeriod: GetNextBusinessPeriod = ({
  todayHours,
  businessStatus,
}) => {
  if (businessStatus === 'openNow' || businessStatus === 'closingSoon') {
    return `Closes ${formatBusinessHour(todayHours.endAt)}`;
  }

  if (businessStatus === 'closedNow' || businessStatus === 'opensSoon') {
    return `Opens ${formatBusinessHour(todayHours.startAt)}`;
  }

  return '';
};

export const getCurrentFullBusinessInfo: GetCurrentFullBusinessInfo = (todayHours) => { // @suppress longLineCheck
  const businessStatus = getBusinessStatus(todayHours);
  const nextBusinessPeriod = getNextBusinessPeriod({ todayHours, businessStatus }); // @suppress longLineCheck
  return {
    businessStatus,
    nextBusinessPeriod,
  };
};

export const getGroupedBusinessHours: GetGroupedBusinessHours = (openingHours) => { // @suppress longLineCheck
  const grouped = openingHours.reduce((acc, current) => {
    const key = `${current.startAt}-${current.endAt}`;

    const newAcc = { ...acc };

    if (!newAcc[key]) {
      newAcc[key] = {
        period: '',
        startAt: current.startAt,
        endAt: current.endAt,
      };
    }

    newAcc[key].period += `${current.day},`;

    return newAcc;
  }, {} as Record<string, { period: string; startAt: string; endAt: string; }>);

  return Object.values(grouped).map((group) => {
    const days = group.period.split(',').filter(day => day).map(
      day => parseInt(day, 10),
    );
    const uniqueDays = [...new Set(days)].sort((a, b) => a - b);

    const dayRanges: { [key: number]: string; } = {
      7: 'Sun',
      1: 'Mon',
      2: 'Tue',
      3: 'Wed',
      4: 'Thu',
      5: 'Fri',
      6: 'Sat',
    };

    const periods: string[] = [];

    let startDay = uniqueDays[0];

    // Iterate over unique days to build day ranges
    for (let i = 1; i <= uniqueDays.length; i += 1) {
      if (uniqueDays[i] !== uniqueDays[i - 1] + 1) {
        const endDay = uniqueDays[i - 1];

        periods.push(
          startDay === endDay
            ? dayRanges[startDay]
            : `${dayRanges[startDay]} - ${dayRanges[endDay]}`,
        );
        startDay = uniqueDays[i];
      }
    }

    const periodRange = periods.join(', ');

    return {
      period: periodRange,
      startAt: formatBusinessHour(group.startAt),
      endAt: formatBusinessHour(group.endAt),
    };
  });
};
